import { getRuntimeApiBase } from "./runtime-api-base";
import { isElectron } from "./platform";
export type * from "./onboarding-types";
import type {
  AiProviderId,
  AiProviderStatus,
  DeskSetupStatus,
  OnboardingBundle,
  OnboardingPermissionId,
  OnboardingPermissionStatus,
  OnboardingSurface,
  OnboardingV2State,
  ProfileRecord,
  SurfaceOnboardingState,
  TraderProfile,
} from "./onboarding-types";

const DEFAULT_API_BASE = getRuntimeApiBase();

export function detectOnboardingSurface(
  override?: OnboardingSurface,
): OnboardingSurface {
  if (override) return override;
  const surface =
    typeof window === "undefined"
      ? ""
      : new URLSearchParams(window.location.search).get("surface");
  if (surface === "desktop" || surface === "mobile" || surface === "web")
    return surface;
  return isElectron() ? "desktop" : "web";
}

export function defaultOnboardingV2(): OnboardingV2State {
  return { version: 2, surfaces: {} };
}

export function parseOnboardingV2(input: unknown): OnboardingV2State {
  if (!input || typeof input !== "object") return defaultOnboardingV2();
  const raw = input as Partial<OnboardingV2State>;
  return {
    completedAt:
      typeof raw.completedAt === "string" ? raw.completedAt : undefined,
    profile: normalizeTraderProfile(raw.profile),
    surfaces:
      raw.surfaces && typeof raw.surfaces === "object" ? raw.surfaces : {},
    version: 2,
  };
}

export function isSurfaceOnboarded(
  onboarding: OnboardingV2State,
  surface: OnboardingSurface,
): boolean {
  const surfaceState = onboarding.surfaces[surface];
  return Boolean(
    onboarding.profile?.nameTag &&
    onboarding.profile?.deskName &&
    surfaceState?.profileComplete &&
    surfaceState.permissions?.status === "complete" &&
    surfaceState.desk?.status &&
    surfaceState.aiProvider?.status &&
    surfaceState.aiProvider.status !== "pending",
  );
}

export function getSurfaceState(
  onboarding: OnboardingV2State,
  surface: OnboardingSurface,
): SurfaceOnboardingState {
  return onboarding.surfaces[surface] ?? { profileComplete: false };
}

export async function fetchOnboardingBundle(
  getAccessToken: () => Promise<string | null>,
  apiBase = DEFAULT_API_BASE,
): Promise<OnboardingBundle> {
  const headers = await authHeaders(getAccessToken);
  const base = apiBase.replace(/\/$/, "");
  const [profileRes, appStateRes] = await Promise.all([
    fetch(`${base}/api/profile`, {
      headers,
      credentials: "include",
    }).catch(() => null),
    fetch(`${base}/api/profile/app-state`, {
      headers,
      credentials: "include",
    }).catch(() => null),
  ]);
  const profile = profileRes?.ok
    ? ((await profileRes.json().catch(() => null)) as ProfileRecord | null)
    : null;
  const appState = appStateRes?.ok
    ? ((await appStateRes.json().catch(() => ({}))) as Record<string, unknown>)
    : {};
  return {
    appState,
    onboarding: parseOnboardingV2(appState.onboardingV2),
    profile,
  };
}

export async function persistTraderProfile(
  getAccessToken: () => Promise<string | null>,
  profile: TraderProfile,
  apiBase = DEFAULT_API_BASE,
): Promise<void> {
  const headers = await authHeaders(getAccessToken);
  const base = apiBase.replace(/\/$/, "");
  const res = await fetch(`${base}/api/profile`, {
    method: "PUT",
    headers,
    credentials: "include",
    body: JSON.stringify({
      avatar_url: profile.avatarUrl,
      display_name: profile.nameTag,
    }),
  });
  if (!res.ok) throw new Error("Profile save failed.");
}

export async function persistOnboardingV2(
  getAccessToken: () => Promise<string | null>,
  onboarding: OnboardingV2State,
  apiBase = DEFAULT_API_BASE,
): Promise<void> {
  const headers = await authHeaders(getAccessToken);
  const base = apiBase.replace(/\/$/, "");
  const res = await fetch(`${base}/api/profile/app-state`, {
    method: "PUT",
    headers,
    credentials: "include",
    body: JSON.stringify({ state: { onboardingV2: onboarding } }),
  });
  if (!res.ok) throw new Error("Onboarding save failed.");
}

export function withProfileComplete(
  onboarding: OnboardingV2State,
  surface: OnboardingSurface,
  profile: TraderProfile,
): OnboardingV2State {
  return {
    ...onboarding,
    profile,
    surfaces: {
      ...onboarding.surfaces,
      [surface]: {
        ...getSurfaceState(onboarding, surface),
        profileComplete: true,
      },
    },
  };
}

export function withPermissionsComplete(
  onboarding: OnboardingV2State,
  surface: OnboardingSurface,
  items: Partial<Record<OnboardingPermissionId, OnboardingPermissionStatus>>,
): OnboardingV2State {
  return {
    ...onboarding,
    surfaces: {
      ...onboarding.surfaces,
      [surface]: {
        ...getSurfaceState(onboarding, surface),
        permissions: {
          completedAt: new Date().toISOString(),
          items,
          status: "complete",
        },
      },
    },
  };
}

export function withDeskComplete(
  onboarding: OnboardingV2State,
  surface: OnboardingSurface,
  input: {
    deskId: string;
    deskName: string;
    status: DeskSetupStatus;
  },
): OnboardingV2State {
  const profile = onboarding.profile
    ? { ...onboarding.profile, deskId: input.deskId, deskName: input.deskName }
    : undefined;
  return {
    ...onboarding,
    profile,
    surfaces: {
      ...onboarding.surfaces,
      [surface]: {
        ...getSurfaceState(onboarding, surface),
        desk: {
          completedAt: new Date().toISOString(),
          deskId: input.deskId,
          deskName: input.deskName,
          status: input.status,
        },
      },
    },
  };
}

export function withAiProviderComplete(
  onboarding: OnboardingV2State,
  surface: OnboardingSurface,
  provider: AiProviderId,
  status: AiProviderStatus,
): OnboardingV2State {
  const completedAt = new Date().toISOString();
  const profileComplete = Boolean(onboarding.profile?.nameTag);
  return {
    ...onboarding,
    completedAt,
    surfaces: {
      ...onboarding.surfaces,
      [surface]: {
        ...getSurfaceState(onboarding, surface),
        aiProvider: {
          completedAt,
          provider,
          status,
          surface,
        },
        completedAt,
        profileComplete,
      },
    },
  };
}

export function cacheProviderSelection(provider: AiProviderId): void {
  try {
    localStorage.setItem("fintheon:default-chat-provider", provider);
    localStorage.setItem("fintheon:harper-provider", provider);
    if (provider === "hermes-grok") {
      localStorage.setItem("fintheon:grok-oauth-status", "set");
    }
    if (provider === "deepseek-direct") {
      localStorage.setItem("fintheon:deepseek-key-status", "set");
    }
    if (provider === "hermes-set-model") {
      localStorage.setItem("fintheon:hermes-set-model-status", "set");
    }
    if (provider === "opencode-go") {
      localStorage.setItem("fintheon:opencode-go-key-status", "set");
    }
  } catch {
    /* ignore */
  }
}

async function authHeaders(
  getAccessToken: () => Promise<string | null>,
): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function normalizeTraderProfile(input: unknown): TraderProfile | undefined {
  if (!input || typeof input !== "object") return undefined;
  const raw = input as Partial<TraderProfile>;
  const nameTag = typeof raw.nameTag === "string" ? raw.nameTag : "";
  const deskName = typeof raw.deskName === "string" ? raw.deskName : "";
  const deskId =
    typeof raw.deskId === "string" && raw.deskId.trim()
      ? raw.deskId
      : "priced-in-capital";
  const brokerOrPropFirm =
    typeof raw.brokerOrPropFirm === "string" ? raw.brokerOrPropFirm : "";
  const yearsExperience =
    typeof raw.yearsExperience === "number" &&
    Number.isFinite(raw.yearsExperience)
      ? raw.yearsExperience
      : 0;
  if (!nameTag && !deskName && !brokerOrPropFirm) return undefined;
  return {
    avatarUrl: typeof raw.avatarUrl === "string" ? raw.avatarUrl : null,
    billingTier: "consul-beta",
    brokerOrPropFirm,
    deskId,
    deskName,
    nameTag,
    yearsExperience,
  };
}
